import {
  ApiError,
  appendErrorEntries,
  Button,
  ButtonColor,
  Control,
  Field,
  Input,
  InputWidth,
  Popover,
  useBillingCurrentPlan,
  useIsFeatureEnabled,
  useToastManager
} from "@quortex/front-common"
import { useMatch } from "@tanstack/react-location"
import { useMutation, useSuspenseQuery } from "@tanstack/react-query"
import type { FC } from "react"
import { Controller, FormProvider, useForm } from "react-hook-form"
import type { TTLFormType } from "@/pages/ContentSteering/Settings/form"
import { useUpdateWorkspace, useWorkspaceList } from "@/queries/workspaces"

interface TTLFormProps {
  ttl: number
}

const TTLForm: FC<TTLFormProps> = ({ ttl }) => {
  const toastManager = useToastManager()
  const { data: billingPlan } = useSuspenseQuery({ ...useBillingCurrentPlan() })

  const { mutateAsync: updateWorkspace } = useMutation(useUpdateWorkspace())
  const { orgUuid } = useMatch().params

  const { data: workspace } = useSuspenseQuery({ ...useWorkspaceList(), select: d => d.results[0] })

  const switchConfigurableSteeringFrequencyEnabled = useIsFeatureEnabled(
    "switch_configurable_steering_frequency"
  )

  const minSteeringRefreshInterval = workspace?.minSteeringRefreshInterval || 30

  const form = useForm<TTLFormType>({
    defaultValues: {
      ttl
    }
  })

  const onTTLChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    form.clearErrors("ttl")
    form.setValue("ttl", Number(e.target.value), { shouldDirty: true })
    form.trigger("ttl")
  }

  const onTTLSubmit = async () => {
    form.clearErrors("ttl")
    const value = form.getValues("ttl")

    if (!value || value < minSteeringRefreshInterval || value > 600) {
      form.setError("ttl", {
        message: `Enter a value between ${minSteeringRefreshInterval} and 600 seconds.`
      })
      return
    }

    try {
      const payload = {
        ttl: value
      }

      const res = await updateWorkspace(payload)
      toastManager.success("Steering refresh interval updated")
      form.reset({ ttl: res.ttl })
    } catch (e) {
      if (e instanceof ApiError) {
        const errorEntries = appendErrorEntries(e.jsonData)
        const [, errorMessage] = errorEntries.find(([path]) => path === "0.message") || [
          "",
          e.message
        ]
        form.setError("ttl", { message: errorMessage })
      }
    }
  }

  return (
    <form>
      <FormProvider {...form}>
        <div className="mt-lg flex flex-col gap-sm">
          <div className="radius-sm flex w-full items-center border border-grey-300 p-sm">
            <div className="typo-sm-medium">
              <Field
                className="flex-row"
                label="Steering refresh interval"
                name="ttl"
                popoverProps={{
                  description:
                    "How often viewers players check for updated CDN steering instructions'",
                  placement: "top"
                }}
              >
                {fieldName => (
                  <Controller
                    control={form.control}
                    name={fieldName}
                    render={({ field }) => (
                      <div className="flex w-full flex-row items-center gap-sm">
                        <Popover
                          description="To edit this setting, upgrade to Premium plan."
                          enabled={
                            billingPlan.name !== "premium" &&
                            !switchConfigurableSteeringFrequencyEnabled
                          }
                          fallbackAxisSideDirection="start"
                          icon={{
                            name: "info",
                            type: "regular"
                          }}
                          links={[
                            {
                              children: "See plans",
                              external: true,
                              href: `${import.meta.env.REACT_APP_PORTAL_URL}/organizations/${orgUuid}/settings/general/billing`
                            }
                          ]}
                          placement="bottom"
                          showTransitions
                          size="default"
                          strategy="absolute"
                          title="Upgrade to premium"
                          trigger="hover"
                          wrapperClassName="flex"
                        >
                          <Control
                            {...field}
                            as={Input}
                            className="flex"
                            data-testid="settings-ttl-input"
                            disabled={
                              billingPlan.name !== "premium" &&
                              !switchConfigurableSteeringFrequencyEnabled
                            }
                            max={600}
                            min={minSteeringRefreshInterval}
                            suffix="secs"
                            type="number"
                            width={InputWidth.SMALL}
                            onChange={onTTLChange}
                          />
                        </Popover>
                        {form.formState.isDirty && (
                          <div className="row flex gap-x-sm">
                            <Button
                              aria-label="Cancel"
                              color={ButtonColor.SECONDARY}
                              data-testid="settings-ttl-cancel-button"
                              disabled={form.formState.isSubmitting}
                              type="button"
                              onClick={() => form.reset()}
                            >
                              Cancel
                            </Button>

                            <Button
                              aria-label="Update"
                              color={ButtonColor.PRIMARY}
                              data-testid="settings-ttl-update-button"
                              disabled={!!form.formState.errors.ttl}
                              isLoading={form.formState.isSubmitting}
                              type="button"
                              onClick={() => onTTLSubmit()}
                            >
                              Update
                            </Button>
                          </div>
                        )}
                      </div>
                    )}
                    rules={{
                      max: {
                        message: `Enter a value between ${minSteeringRefreshInterval} and 600 seconds.`,
                        value: 600
                      },
                      min: {
                        message: `Enter a value between ${minSteeringRefreshInterval} and 600 seconds.`,
                        value: minSteeringRefreshInterval
                      }
                    }}
                  />
                )}
              </Field>
            </div>
          </div>
        </div>
      </FormProvider>
    </form>
  )
}

export default TTLForm
